import React, { useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Switch,
  KeyboardAvoidingView,
  Platform,
  useColorScheme,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import { useCreatePoll } from '../hooks/useCreatePoll';

interface CreatePollModalProps {
  visible: boolean;
  onClose: () => void;
  chatId: string;
  userId: string;
  onPollCreated?: (pollId: string) => void;
}

/**
 * Modal for creating a new poll
 */
export const CreatePollModal: React.FC<CreatePollModalProps> = ({
  visible,
  onClose,
  chatId,
  userId,
  onPollCreated,
}) => {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === 'dark';
  const { createPoll, loading, error, clearError } = useCreatePoll();

  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [hasDeadline, setHasDeadline] = useState(false);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const colors = {
    background: isDark ? '#111827' : '#FFFFFF',
    surface: isDark ? '#1F2937' : '#F3F4F6',
    text: isDark ? '#F9FAFB' : '#111827',
    textSecondary: isDark ? '#9CA3AF' : '#6B7280',
    border: isDark ? '#374151' : '#E5E7EB',
    primary: '#7C3AED',
    danger: '#EF4444',
  };

  const handleAddOption = () => {
    if (options.length < 10) {
      setOptions([...options, '']);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleSubmit = async () => {
    const pollId = await createPoll(
      {
        chat_id: chatId,
        question,
        options,
        is_anonymous: isAnonymous,
        allows_multiple_answers: allowMultiple,
        closes_at: hasDeadline && deadline ? deadline : null,
      },
      userId
    );

    if (pollId) {
      // Reset form
      setQuestion('');
      setOptions(['', '']);
      setIsAnonymous(false);
      setAllowMultiple(false);
      setHasDeadline(false);
      setDeadline(null);
      onPollCreated?.(pollId);
      onClose();
    }
  };

  const handleDateChange = (event: any, selectedDate?: Date) => {
    setShowDatePicker(false);
    if (selectedDate) {
      // Preserve time if already set, or default to current time + 1 hour
      const currentTime = deadline || new Date();
      selectedDate.setHours(currentTime.getHours(), currentTime.getMinutes());
      setDeadline(selectedDate);
      // Show time picker after date is selected
      if (Platform.OS === 'android') {
        setTimeout(() => setShowTimePicker(true), 100);
      }
    }
  };

  const handleTimeChange = (event: any, selectedTime?: Date) => {
    setShowTimePicker(false);
    if (selectedTime && deadline) {
      const newDeadline = new Date(deadline);
      newDeadline.setHours(selectedTime.getHours(), selectedTime.getMinutes());
      setDeadline(newDeadline);
    }
  };

  const formatDeadline = (date: Date | null): string => {
    if (!date) return 'Selecteer datum & tijd';
    return date.toLocaleString('nl-NL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMinDate = (): Date => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // At least 5 minutes from now
    return now;
  };

  const handleClose = () => {
    clearError();
    onClose();
  };

  const isValid = question.trim() && options.filter((o) => o.trim()).length >= 2;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={{ flex: 1 }}
        >
          {/* Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: colors.border }}
          >
            <TouchableOpacity onPress={handleClose} className="p-2">
              <Text style={{ color: colors.primary }} className="text-base font-medium">
                Annuleren
              </Text>
            </TouchableOpacity>
            <Text style={{ color: colors.text }} className="text-lg font-bold">
              Nieuwe Poll
            </Text>
            <TouchableOpacity
              onPress={handleSubmit}
              disabled={!isValid || loading}
              className="p-2"
            >
              {loading ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <Text
                  style={{ color: isValid ? colors.primary : colors.textSecondary }}
                  className="text-base font-semibold"
                >
                  Maak
                </Text>
              )}
            </TouchableOpacity>
          </View>

          <ScrollView className="flex-1 px-4 py-4" showsVerticalScrollIndicator={false}>
            {/* Error Message */}
            {error && (
              <View className="bg-red-100 dark:bg-red-900/30 rounded-xl p-3 mb-4">
                <Text className="text-red-600 dark:text-red-400 text-sm">{error}</Text>
              </View>
            )}

            {/* Question Input */}
            <View className="mb-6">
              <Text style={{ color: colors.text }} className="text-base font-semibold mb-2">
                Vraag
              </Text>
              <TextInput
                value={question}
                onChangeText={setQuestion}
                placeholder="Stel een vraag..."
                placeholderTextColor={colors.textSecondary}
                style={{
                  backgroundColor: colors.surface,
                  color: colors.text,
                  borderColor: colors.border,
                }}
                className="rounded-xl px-4 py-3 text-base border"
                multiline
                maxLength={500}
              />
            </View>

            {/* Options */}
            <View className="mb-6">
              <Text style={{ color: colors.text }} className="text-base font-semibold mb-2">
                Opties
              </Text>
              {options.map((option, index) => (
                <View key={index} className="flex-row items-center mb-2">
                  <View className="flex-1 flex-row items-center">
                    <View
                      className="w-8 h-8 rounded-full items-center justify-center mr-2"
                      style={{ backgroundColor: colors.primary + '20' }}
                    >
                      <Text style={{ color: colors.primary }} className="font-bold">
                        {index + 1}
                      </Text>
                    </View>
                    <TextInput
                      value={option}
                      onChangeText={(value) => handleOptionChange(index, value)}
                      placeholder={`Optie ${index + 1}`}
                      placeholderTextColor={colors.textSecondary}
                      style={{
                        backgroundColor: colors.surface,
                        color: colors.text,
                        borderColor: colors.border,
                        flex: 1,
                      }}
                      className="rounded-xl px-4 py-3 text-base border"
                      maxLength={100}
                    />
                  </View>
                  {options.length > 2 && (
                    <TouchableOpacity
                      onPress={() => handleRemoveOption(index)}
                      className="ml-2 p-2"
                    >
                      <Ionicons name="close-circle" size={24} color={colors.danger} />
                    </TouchableOpacity>
                  )}
                </View>
              ))}

              {options.length < 10 && (
                <TouchableOpacity
                  onPress={handleAddOption}
                  className="flex-row items-center justify-center py-3 mt-2 rounded-xl"
                  style={{ backgroundColor: colors.surface }}
                >
                  <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                  <Text style={{ color: colors.primary }} className="font-medium ml-2">
                    Optie toevoegen
                  </Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Settings */}
            <View className="mb-6">
              <Text style={{ color: colors.text }} className="text-base font-semibold mb-3">
                Instellingen
              </Text>

              {/* Anonymous voting */}
              <View
                className="flex-row items-center justify-between py-4 border-b"
                style={{ borderColor: colors.border }}
              >
                <View className="flex-1 mr-4">
                  <Text style={{ color: colors.text }} className="text-base font-medium">
                    Anoniem stemmen
                  </Text>
                  <Text style={{ color: colors.textSecondary }} className="text-sm mt-0.5">
                    Stemmen worden niet getoond aan anderen
                  </Text>
                </View>
                <Switch
                  value={isAnonymous}
                  onValueChange={setIsAnonymous}
                  trackColor={{ false: colors.border, true: colors.primary + '80' }}
                  thumbColor={isAnonymous ? colors.primary : '#FFFFFF'}
                />
              </View>

              {/* Multiple answers */}
              <View
                className="flex-row items-center justify-between py-4 border-b"
                style={{ borderColor: colors.border }}
              >
                <View className="flex-1 mr-4">
                  <Text style={{ color: colors.text }} className="text-base font-medium">
                    Meerdere antwoorden
                  </Text>
                  <Text style={{ color: colors.textSecondary }} className="text-sm mt-0.5">
                    Gebruikers kunnen meerdere opties kiezen
                  </Text>
                </View>
                <Switch
                  value={allowMultiple}
                  onValueChange={setAllowMultiple}
                  trackColor={{ false: colors.border, true: colors.primary + '80' }}
                  thumbColor={allowMultiple ? colors.primary : '#FFFFFF'}
                />
              </View>

              {/* Deadline */}
              <View className="py-4">
                <View className="flex-row items-center justify-between">
                  <View className="flex-1 mr-4">
                    <Text style={{ color: colors.text }} className="text-base font-medium">
                      Deadline
                    </Text>
                    <Text style={{ color: colors.textSecondary }} className="text-sm mt-0.5">
                      Poll sluit automatisch na deze tijd
                    </Text>
                  </View>
                  <Switch
                    value={hasDeadline}
                    onValueChange={(value) => {
                      setHasDeadline(value);
                      if (value && !deadline) {
                        // Set default deadline to 24 hours from now
                        const defaultDeadline = new Date();
                        defaultDeadline.setDate(defaultDeadline.getDate() + 1);
                        setDeadline(defaultDeadline);
                      }
                    }}
                    trackColor={{ false: colors.border, true: colors.primary + '80' }}
                    thumbColor={hasDeadline ? colors.primary : '#FFFFFF'}
                  />
                </View>

                {hasDeadline && (
                  <View className="mt-3">
                    <Pressable
                      onPress={() => setShowDatePicker(true)}
                      className="flex-row items-center p-3 rounded-xl"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <Ionicons name="calendar-outline" size={20} color={colors.primary} />
                      <Text style={{ color: colors.text }} className="flex-1 ml-3 text-base">
                        {formatDeadline(deadline)}
                      </Text>
                      <Ionicons name="chevron-forward" size={20} color={colors.textSecondary} />
                    </Pressable>

                    {Platform.OS === 'ios' && (
                      <Pressable
                        onPress={() => setShowTimePicker(true)}
                        className="flex-row items-center p-3 rounded-xl mt-2"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="time-outline" size={20} color={colors.primary} />
                        <Text style={{ color: colors.text }} className="flex-1 ml-3 text-base">
                          {deadline
                            ? deadline.toLocaleTimeString('nl-NL', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'Selecteer tijd'}
                        </Text>
                        <Ionicons name="chevron-forward" size={20} color={colors.textSecondary} />
                      </Pressable>
                    )}
                  </View>
                )}
              </View>
            </View>

            {/* Date Picker Modal */}
            {showDatePicker && (
              <DateTimePicker
                value={deadline || getMinDate()}
                mode="date"
                display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                minimumDate={getMinDate()}
                onChange={handleDateChange}
              />
            )}

            {/* Time Picker Modal */}
            {showTimePicker && (
              <DateTimePicker
                value={deadline || new Date()}
                mode="time"
                display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                onChange={handleTimeChange}
              />
            )}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
};

